import { useCallback, useMemo, useRef, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Dialog } from '../../components/ui/Dialog';
import { Department, CreateDepartmentInput, UpdateDepartmentInput } from '../../lib/adminApi';
import { requestJson, getErrorMessage, isAbortError } from '../../lib/adminApi';
import { useToast } from '../../components/ui/Toast';

type DepartmentStatusFilter = 'all' | 'active' | 'inactive';
type DepartmentSortField = 'name' | 'code' | 'employeeCount' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

const DEPARTMENT_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle Status' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
];

const DEPARTMENT_SORT_OPTIONS: Array<{ value: DepartmentSortField; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'code', label: 'Kürzel' },
  { value: 'employeeCount', label: 'Mitarbeiter' },
  { value: 'createdAt', label: 'Erstellt' },
  { value: 'updatedAt', label: 'Aktualisiert' },
];

export function DepartmentManagementPage() {
  const { addToast } = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DepartmentStatusFilter>('all');
  const [sortField, setSortField] = useState<DepartmentSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [form, setForm] = useState<CreateDepartmentInput>({ name: '', code: '', description: undefined, isActive: true });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{ variant: 'error'; text: string } | null>(null);

  const requestRef = useRef<AbortController | null>(null);

  const loadDepartments = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('sortBy', sortField);
      params.set('sortOrder', sortDirection);
      const data = await requestJson<Department[]>(`/departments?${params.toString()}`, { signal: controller.signal });
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isAbortError(err)) {
        setError(getErrorMessage(err, 'Abteilungen konnten nicht geladen werden.'));
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
    }
  }, [search, statusFilter, sortField, sortDirection]);

  const handleToggleStatus = async (department: Department) => {
    try {
      await requestJson<Department>(`/departments/${department.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !department.isActive }),
      });
      addToast({
        title: department.isActive ? 'Abteilung deaktiviert' : 'Abteilung aktiviert',
        description: `Die Abteilung "${department.name}" wurde ${department.isActive ? 'deaktiviert' : 'aktiviert'}.`,
        variant: 'success',
      });
      void loadDepartments();
    } catch (err) {
      addToast({
        title: 'Fehler',
        description: getErrorMessage(err, 'Status konnte nicht geändert werden.'),
        variant: 'error',
      });
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await requestJson<Department>('/departments', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setCreateDialogOpen(false);
      setForm({ name: '', code: '', description: undefined, isActive: true });
      addToast({ title: 'Abteilung erstellt', description: 'Die Abteilung wurde erfolgreich angelegt.', variant: 'success' });
      void loadDepartments();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Abteilung konnte nicht erstellt werden.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDepartment) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const input: UpdateDepartmentInput = {};
      if (form.name !== undefined) input.name = form.name;
      if (form.code !== undefined) input.code = form.code;
      if (form.description !== undefined) input.description = form.description;
      if (form.isActive !== undefined) input.isActive = form.isActive;
      await requestJson<Department>(`/departments/${editingDepartment.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      setEditDialogOpen(false);
      setEditingDepartment(null);
      setForm({ name: '', code: '', description: undefined, isActive: true });
      addToast({ title: 'Abteilung aktualisiert', description: 'Die Abteilung wurde erfolgreich geändert.', variant: 'success' });
      void loadDepartments();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Abteilung konnte nicht aktualisiert werden.'));
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (department: Department) => {
    setEditingDepartment(department);
    setForm({
      name: department.name,
      code: department.code,
      description: department.description ?? undefined,
      isActive: department.isActive,
    });
    setFormError(null);
    setEditDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingDepartment(null);
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
  };

  const initiateDelete = (department: Department) => {
    setDeleteTarget(department);
    setDeleteMessage(null);
    setConfirmDeleteOpen(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteMessage(null);
    try {
      await requestJson<{ success: boolean }>(`/departments/${deleteTarget.id}`, { method: 'DELETE' });
      setConfirmDeleteOpen(false);
      setDeleteTarget(null);
      addToast({ title: 'Abteilung gelöscht', description: 'Die Abteilung wurde entfernt.', variant: 'success' });
      void loadDepartments();
    } catch (err) {
      const message = getErrorMessage(err, 'Abteilung konnte nicht gelöscht werden.');
      setDeleteMessage({ variant: 'error', text: message });
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredDepartments = useMemo(() => {
    const query = search.trim().toLowerCase();
    return departments
      .filter((dept) => {
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && dept.isActive) ||
          (statusFilter === 'inactive' && !dept.isActive);
        return matchesStatus && (!query || dept.name.toLowerCase().includes(query) || dept.code.toLowerCase().includes(query));
      })
      .sort((a, b) => {
        let result = 0;
        switch (sortField) {
          case 'code':
            result = a.code.localeCompare(b.code, 'de-DE');
            break;
          case 'employeeCount':
            result = a.employeeCount - b.employeeCount;
            break;
          case 'createdAt':
            result = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            break;
          case 'updatedAt':
            result = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
            break;
          case 'name':
          default:
            result = a.name.localeCompare(b.name, 'de-DE');
        }
        return sortDirection === 'asc' ? result : -result;
      });
  }, [departments, search, statusFilter, sortField, sortDirection]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Organisation', href: '/admin/organization' },
    { label: 'Abteilungen', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Abteilungen</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Abteilungen verwalten, suchen, filtern und sortieren.
          </p>
        </div>
        <Button variant="primary" leftIcon="Plus" onClick={openCreateDialog}>
          Abteilung anlegen
        </Button>
      </div>

      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader className="mb-4">
          <CardTitle>Abteilungsliste</CardTitle>
          <CardDescription>
            {filteredDepartments.length} von {departments.length} Abteilungen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && departments.length === 0 ? (
            <ErrorState description={error} onRetry={loadDepartments} />
          ) : error ? (
            <Alert variant="error" title="Abteilungen konnten nicht vollständig geladen werden" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_4rem]">
            <Input
              aria-label="Abteilungen durchsuchen"
              placeholder="Name, Kürzel suchen ..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              leftIcon="Search"
            />
            <Select
              aria-label="Status filtern"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as DepartmentStatusFilter)}
              options={DEPARTMENT_STATUS_OPTIONS}
            />
            <Select
              aria-label="Abteilungen sortieren"
              value={sortField}
              onChange={(event) => setSortField(event.target.value as DepartmentSortField)}
              options={DEPARTMENT_SORT_OPTIONS}
            />
            <Button
              variant="outline"
              size="sm"
              aria-label="Sortierrichtung wechseln"
              onClick={() => setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))}
            >
              {sortDirection === 'asc' ? <icons.ChevronUp className="h-4 w-4" /> : <icons.ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          {loading && departments.length === 0 ? (
            <SkeletonTable rows={6} columns={5} />
          ) : filteredDepartments.length === 0 ? (
            <EmptyState
              icon="Building2"
              title="Keine Abteilungen gefunden"
              description="Passende Abteilungen sind nicht vorhanden oder die aktuellen Filter liefern keine Ergebnisse."
              action={
                <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                  Filter zurücksetzen
                </Button>
              }
            />
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  className: 'min-w-[180px]',
                  render: (dept: Department) => <span className="font-medium text-neutral-900 dark:text-white">{dept.name}</span>,
                },
                {
                  key: 'code',
                  header: 'Kürzel',
                  className: 'w-24',
                  render: (dept: Department) => <span className="font-mono text-sm text-neutral-600 dark:text-neutral-400">{dept.code}</span>,
                },
                {
                  key: 'employeeCount',
                  header: 'Mitarbeiter',
                  className: 'w-24',
                  render: (dept: Department) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{dept.employeeCount}</span>,
                },
                {
                  key: 'isActive',
                  header: 'Status',
                  className: 'w-24',
                  render: (dept: Department) => (
                    <Badge variant={dept.isActive ? 'medical' : 'gray'}>{dept.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Aktionen',
                  className: 'w-52',
                  render: (dept: Department) => (
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(dept); }}>
                        Bearbeiten
                      </Button>
                      <Button
                        variant={dept.isActive ? 'outline' : 'primary'}
                        size="sm"
                        leftIcon={dept.isActive ? 'UserX' : 'UserCheck'}
                        onClick={(e) => { e.stopPropagation(); handleToggleStatus(dept); }}
                      >
                        {dept.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon="Trash2"
                        onClick={(e) => { e.stopPropagation(); initiateDelete(dept); }}
                      >
                        Löschen
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredDepartments}
              keyExtractor={(dept) => dept.id}
              loading={loading && departments.length > 0}
              emptyMessage="Keine Abteilungen entsprechen den aktuellen Filtern."
              striped
              hoverable
              compact
            />
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        title="Neue Abteilung"
        description="Eine neue Abteilung anlegen."
        size="md"
        closeOnBackdrop={!submitting}
        closeOnEscape={!submitting}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {formError && (
            <Alert variant="error" title="Fehler" dismissible onDismiss={() => setFormError(null)}>
              <p>{formError}</p>
            </Alert>
          )}
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="z. B. Innere Medizin"
            required
            autoFocus
          />
          <Input
            label="Kürzel"
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder="z. B. IM"
            required
          />
          <Input
            label="Beschreibung (optional)"
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value || null }))}
            placeholder="Kurze Beschreibung der Abteilung..."
          />
          <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
            <Button variant="secondary" type="button" onClick={closeCreateDialog} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting || !form.name.trim() || !form.code.trim()}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        title={`Abteilung bearbeiten${editingDepartment ? `: ${editingDepartment.name}` : ''}`}
        description="Die Abteilungsdaten ändern."
        size="md"
        closeOnBackdrop={!submitting}
        closeOnEscape={!submitting}
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          {formError && (
            <Alert variant="error" title="Fehler" dismissible onDismiss={() => setFormError(null)}>
              <p>{formError}</p>
            </Alert>
          )}
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            autoFocus
          />
          <Input
            label="Kürzel"
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            required
          />
          <Input
            label="Beschreibung (optional)"
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value || null }))}
            placeholder="Kurze Beschreibung der Abteilung..."
          />
          <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
            <Button variant="secondary" type="button" onClick={closeEditDialog} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting || !form.name.trim() || !form.code.trim()}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => { setConfirmDeleteOpen(false); setDeleteMessage(null); }}
        title="Abteilung löschen"
        description={deleteTarget ? `Möchten Sie die Abteilung "${deleteTarget.name}" wirklich löschen?` : ''}
        size="sm"
        closeOnBackdrop={!deleteLoading}
        closeOnEscape={!deleteLoading}
      >
        {deleteMessage && (
          <Alert variant={deleteMessage.variant} dismissible onDismiss={() => setDeleteMessage(null)}>
            <p>{deleteMessage.text}</p>
          </Alert>
        )}
        <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
          <Button variant="secondary" type="button" onClick={() => { setConfirmDeleteOpen(false); setDeleteMessage(null); }} disabled={deleteLoading}>
            Abbrechen
          </Button>
          <Button variant="danger" type="button" loading={deleteLoading} onClick={executeDelete}>
            Löschen
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
